// SS-1 — upload queue. Local data is removed only after ss1_register_media has verified the stored object; failures stay on the device.
import * as L from '@/lib/snag/db';
import { uploadAndRegister, sha256, extOf } from '@/lib/snag/service';

const listeners = new Set(); const running = new Map();
export const onSync = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const notify = (inspectionId) => listeners.forEach((fn) => fn(inspectionId));

async function pushSegment(org, inspectionId, seg) {
  const blob = await L.segmentBlob(seg); if (!blob) { await L.del('segments', seg.id); return; }
  seg.state = 'uploading'; seg.error = ''; await L.put('segments', seg); notify(inspectionId);
  try {
    seg.sha256 = seg.sha256 || await sha256(blob); seg.byteSize = blob.size;
    const path = `${org}/${inspectionId}/audio/${String(seg.seq).padStart(3, '0')}-${seg.id}.${extOf(seg.mime)}`;
    await uploadAndRegister({ bucket: 'inspection-audio', path, blob, mime: seg.mime, inspectionId, kind: 'audio', item: seg });
    seg.state = 'uploaded'; seg.uploadedAt = Date.now(); await L.put('segments', seg); await L.dropChunks(seg.id);
  } catch (e) { seg.state = 'failed'; seg.error = e.message; await L.put('segments', seg); }
  notify(inspectionId);
}
async function pushPhoto(org, inspectionId, ph) {
  if (!ph.blob) { ph.state = 'uploaded'; await L.put('photos', ph); return; }
  ph.state = 'uploading'; ph.error = ''; await L.put('photos', ph); notify(inspectionId);
  try {
    ph.sha256 = ph.sha256 || await sha256(ph.blob); ph.byteSize = ph.blob.size;
    const path = `${org}/${inspectionId}/photos/${String(ph.seq).padStart(3, '0')}-${ph.id}.${extOf(ph.mime)}`;
    await uploadAndRegister({ bucket: 'inspection-photos', path, blob: ph.blob, mime: ph.mime, inspectionId, kind: 'photo', item: ph });
    ph.state = 'uploaded'; ph.uploadedAt = Date.now(); ph.blob = null; await L.put('photos', ph);
  } catch (e) { ph.state = 'failed'; ph.error = e.message; await L.put('photos', ph); }
  notify(inspectionId);
}

// Uploads everything closed/captured (and, with retryFailed, anything failed) for one inspection. One runner per inspection.
export async function syncInspection(org, inspectionId, { retryFailed = false } = {}) {
  if (running.get(inspectionId)) return running.get(inspectionId);
  const p = (async () => {
    if (!navigator.onLine) return;
    const want = (st) => st === 'closed' || st === 'captured' || (retryFailed && st === 'failed');
    for (const s of await L.segmentsOf(inspectionId)) if (want(s.state)) await pushSegment(org, inspectionId, s);
    for (const ph of await L.photosOf(inspectionId)) if (want(ph.state)) await pushPhoto(org, inspectionId, ph);
  })().finally(() => running.delete(inspectionId));
  running.set(inspectionId, p); return p;
}

export async function localSummary(inspectionId) {
  const segs = await L.segmentsOf(inspectionId), photos = await L.photosOf(inspectionId);
  const count = (rows, st) => rows.filter((r) => st.includes(r.state)).length;
  return {
    segs, photos,
    audioDevice: count(segs, ['recording', 'closed', 'uploading', 'failed']), audioCloud: count(segs, ['uploaded']), audioFailed: count(segs, ['failed']),
    photoDevice: count(photos, ['captured', 'uploading', 'failed']), photoCloud: count(photos, ['uploaded']), photoFailed: count(photos, ['failed']),
    pending: count(segs, ['recording', 'closed', 'uploading', 'failed']) + count(photos, ['captured', 'uploading', 'failed']),
    manifest: [...segs.map((s) => s.id), ...photos.map((p) => p.id)],
    recordedMs: segs.reduce((a, s) => a + (s.durationMs || 0), 0),
  };
}
